import { useRouter } from 'next/router';
import { Text } from 'components/primitives/typography';
import { useOnSuccessActionModal } from 'components/inbox/hooks/useOnSuccessActionModal';
import useModal from 'components/remodal/v2/useModal';
import { useState } from 'react';
import Button from 'components/ui/Button/Button';
import CaptainBookingActionsModal from '../../../../../pages/a/bookings/[bookingId]/captain_action/[action]';
import { Flex, FlexCol } from '../../primitives/flex';

export const approveAction = (booking, openApproveModal) => {
  if (booking.can_captain_approve) {
    return {
      actionText: 'Approve',
      available: true,
      onClick: openApproveModal,
    };
  }
  if (booking.conflicting_dates || booking.waiting_on_renter_verification) {
    let reason = '';
    if (booking.conflicting_dates) {
      reason = 'This boat is unavailable on these dates.';
    } else if (booking.waiting_on_renter_verification) {
      reason = 'The renter has not yet completed his or her renter verification.';
    }
    return {
      actionText: reason,
      available: true,
      onClick: () => null,
      textOnly: true,
    };
  }
  return {
    actionText: '',
    available: false,
    onClick: () => null,
  };
};

const SidebarCaptainActions = ({ booking, hideBookingActions }) => {
  const router = useRouter();
  const onSuccess = useOnSuccessActionModal();

  const [captainActionType, setCaptainActionType] = useState<'approve' | 'decline'>('decline');
  const { modal: captainBookingActionsModal, openModal: openCaptainBookingActionsModal } = useModal(
    <CaptainBookingActionsModal booking={booking} actionType={captainActionType} onSuccess={onSuccess} />,
    {
      size: captainActionType === 'approve' ? 'large' : 'medium',
      overridePadding: captainActionType === 'approve' ? undefined : 'small',
      trackingName: 'captain_booking_actions_modal',
    }
  );
  const handleOpenCaptainActionModal = (actionType: 'approve' | 'decline') => {
    setCaptainActionType(actionType);
    openCaptainBookingActionsModal();
  };

  const approve = approveAction(booking, () => handleOpenCaptainActionModal('approve'));
  const buttons = [
    {
      actionText: approve.actionText || 'Approve',
      available: !hideBookingActions && approve.available,
      onClick: approve.onClick,
      textOnly: approve.textOnly,
    },
    {
      actionText: 'Decline',
      available: !hideBookingActions && booking.can_captain_decline && !booking.captain_declined,
      onClick: () => handleOpenCaptainActionModal('decline'),
      outline: true,
    },
    {
      actionText: 'Leave review',
      available: ['concluded', 'ashore', 'disputed'].includes(booking.state) && booking.show_captain_leave_a_review,
      onClick: () => {
        router.push(`/a/bookings/${booking.id}/review`);
      },
    },
    {
      actionText: 'View booking details',
      available: booking.can_captain_view_details && booking.can_view_details && booking.state !== 'expired',
      onClick: () => {
        window.location.href = booking.details_url;
      },
      outline: true,
    },
  ].filter((b) => b.available);

  const renderButton = (button, index) => {
    if (buttons.length > 2 && index === 2) {
      return (
        <Button
          fullWidth
          type="button"
          variant="captain"
          justText
          onClick={button.onClick}
          text={
            <Text fWeight="500" as="span">
              {button.actionText}
            </Text>
          }
        />
      );
    }
    return (
      <Button
        fullWidth
        onClick={button.onClick}
        type="button"
        variant={index !== 0 ? 'darkCaptain' : 'captain'}
        text={
          button.textOnly ? (
            <Text fWeight="500" as="span">
              {button.actionText}
            </Text>
          ) : (
            button.actionText
          )
        }
        outline={index !== 0}
        className={`${index !== 0 ? 'Button--darkAquamarineOutline' : 'Button--aquamarine'}`}
      />
    );
  };

  return (
    <FlexCol gap="15px">
      {buttons.map((button, index) => (
        <Flex key={button.actionText}>{button.textOnly ? button.actionText : renderButton(button, index)}</Flex>
      ))}

      {captainBookingActionsModal}
    </FlexCol>
  );
};

export default SidebarCaptainActions;
